import { useEffect, useState, useCallback, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  X, RefreshCw, BookOpen, Plus, Upload, FileText, Check,
  Star, StarOff, Trash2, ArrowLeft, Save, AlertTriangle,
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// ==================== version list ====================
const VersionList = ({ bibles, onOpen, onActivate, onDelete, onCreate, onImport, onPaste, loading }) => (
  <div className="space-y-3" data-testid="bible-version-list">
    <div className="grid grid-cols-3 gap-2">
      <button
        onClick={onCreate}
        data-testid="bible-action-create"
        className="flex flex-col items-center gap-1 p-3 border border-neon-red/30 hover:border-neon-red bg-black/40 text-white/80 hover:text-white transition-all"
      >
        <Plus className="w-4 h-4" />
        <span className="font-jetbrains text-[10px] tracking-widest uppercase">Nueva</span>
      </button>
      <button
        onClick={onPaste}
        data-testid="bible-action-paste"
        className="flex flex-col items-center gap-1 p-3 border border-neon-red/30 hover:border-neon-red bg-black/40 text-white/80 hover:text-white transition-all"
      >
        <FileText className="w-4 h-4" />
        <span className="font-jetbrains text-[10px] tracking-widest uppercase">Pegar</span>
      </button>
      <button
        onClick={onImport}
        data-testid="bible-action-import"
        className="flex flex-col items-center gap-1 p-3 border border-neon-red/30 hover:border-neon-red bg-black/40 text-white/80 hover:text-white transition-all"
      >
        <Upload className="w-4 h-4" />
        <span className="font-jetbrains text-[10px] tracking-widest uppercase">Importar</span>
      </button>
    </div>

    {loading && (
      <p className="font-jetbrains text-xs text-white/50 text-center py-4">Cargando...</p>
    )}

    {bibles.length === 0 && !loading && (
      <div className="p-4 border border-white/10 bg-black/40 text-center">
        <p className="font-jetbrains text-xs text-white/60">
          Sin Bibles todavía. Crea una vacía, pega texto o importa un archivo.
        </p>
      </div>
    )}

    {bibles.map(b => (
      <div
        key={b.bible_id}
        data-testid={`bible-version-${b.bible_id}`}
        className={`p-3 border ${b.active ? 'border-emerald-400/50 bg-emerald-950/20' : 'border-white/10 bg-black/40'} flex items-start justify-between gap-2`}
      >
        <button
          onClick={() => onOpen(b.bible_id)}
          className="flex-1 min-w-0 text-left"
          data-testid={`bible-open-${b.bible_id}`}
        >
          <div className="flex items-center gap-2">
            {b.active
              ? <Star className="w-4 h-4 text-emerald-400 flex-shrink-0" />
              : <StarOff className="w-4 h-4 text-white/30 flex-shrink-0" />}
            <span className="font-outfit text-sm text-white truncate">
              v{b.version} — {b.name}
            </span>
          </div>
          <div className="font-jetbrains text-[10px] text-white/50 mt-1">
            {b.source} · {new Date(b.updated_at).toLocaleString()}
          </div>
        </button>
        <div className="flex flex-col gap-1">
          {!b.active && (
            <button
              onClick={() => onActivate(b.bible_id)}
              data-testid={`bible-activate-${b.bible_id}`}
              className="p-1.5 text-emerald-300 hover:text-emerald-100 border border-emerald-400/30 hover:border-emerald-400/70"
              title="Activar"
            >
              <Check className="w-3 h-3" />
            </button>
          )}
          {!b.active && (
            <button
              onClick={() => onDelete(b.bible_id)}
              data-testid={`bible-delete-${b.bible_id}`}
              className="p-1.5 text-white/40 hover:text-neon-red border border-white/10 hover:border-neon-red/60"
              title="Borrar"
            >
              <Trash2 className="w-3 h-3" />
            </button>
          )}
        </div>
      </div>
    ))}
  </div>
);

// ==================== structured editor ====================
const BibleEditor = ({ projectSlug, bibleId, initialData, schema, supportsMeta, sourceTypes, confidenceLevels, onBack, onSaved, mode }) => {
  const [name, setName] = useState(initialData?.name || '');
  const [fields, setFields] = useState(() => {
    const seed = {};
    (schema || []).forEach(({ key }) => { seed[key] = ''; });
    if (initialData?.fields) Object.assign(seed, initialData.fields);
    return seed;
  });
  const [fieldMeta, setFieldMeta] = useState(() => {
    const seed = {};
    (schema || []).forEach(({ key }) => {
      seed[key] = { source_type: '', source_reference: '', confidence: '', notes: '' };
    });
    if (initialData?.field_meta) {
      Object.entries(initialData.field_meta).forEach(([k, v]) => {
        if (seed[k]) seed[k] = { ...seed[k], ...(v || {}) };
      });
    }
    return seed;
  });
  const [expandedMeta, setExpandedMeta] = useState({});
  const [rawText, setRawText] = useState(initialData?.raw_text || '');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [activate, setActivate] = useState(false);

  const isEdit = mode === 'edit';
  const isPaste = mode === 'paste';

  const save = async () => {
    setSaving(true);
    setError(null);
    try {
      const payload = { name, fields, raw_text: rawText };
      if (supportsMeta) payload.field_meta = fieldMeta;
      if (isEdit && bibleId) {
        const res = await fetch(`${API}/bibles/${bibleId}`, {
          method: 'PUT',
          credentials: 'include',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(payload),
        });
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const data = await res.json();
        onSaved(data.bible);
      } else {
        const res = await fetch(`${API}/projects/${projectSlug}/bibles`, {
          method: 'POST',
          credentials: 'include',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            ...payload,
            name: name || null,
            activate,
            source: isPaste ? 'paste' : 'manual',
          }),
        });
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const data = await res.json();
        onSaved(data.bible);
      }
    } catch (e) {
      setError('No se pudo guardar: ' + e.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-4" data-testid="bible-editor">
      <div className="flex items-center justify-between">
        <button
          onClick={onBack}
          data-testid="bible-editor-back"
          className="flex items-center gap-1 font-jetbrains text-[10px] tracking-widest uppercase text-white/60 hover:text-white"
        >
          <ArrowLeft className="w-3 h-3" /> Volver
        </button>
        <button
          onClick={save}
          disabled={saving}
          data-testid="bible-editor-save"
          className="flex items-center gap-1.5 px-3 py-2 bg-neon-red/20 border border-neon-red hover:bg-neon-red/40 text-white font-jetbrains text-xs tracking-widest uppercase disabled:opacity-50"
        >
          <Save className="w-3 h-3" /> {saving ? 'Guardando...' : (isEdit ? 'Guardar cambios' : 'Guardar')}
        </button>
      </div>

      {error && (
        <div className="p-2 border border-neon-red/50 bg-neon-red/10 font-jetbrains text-xs text-neon-red" data-testid="bible-editor-error">
          {error}
        </div>
      )}

      <div>
        <label className="font-jetbrains text-[10px] tracking-widest uppercase text-white/50">Nombre de esta versión</label>
        <input
          type="text"
          value={name}
          onChange={e => setName(e.target.value)}
          data-testid="bible-name-input"
          placeholder={`Bible ${projectSlug} v?`}
          className="mt-1 w-full bg-black/60 border border-white/10 px-3 py-2 text-white font-jetbrains text-sm focus:border-neon-red outline-none"
        />
      </div>

      {isPaste && (
        <div>
          <label className="font-jetbrains text-[10px] tracking-widest uppercase text-white/50">
            Texto libre (opcional; permite pegar la Bible completa)
          </label>
          <textarea
            value={rawText}
            onChange={e => setRawText(e.target.value)}
            data-testid="bible-raw-text"
            rows={8}
            placeholder="Pega aquí el texto de la Bible..."
            className="mt-1 w-full bg-black/60 border border-white/10 px-3 py-2 text-white font-jetbrains text-xs focus:border-neon-red outline-none resize-y"
          />
        </div>
      )}

      <div className="space-y-3">
        <p className="font-jetbrains text-[10px] tracking-widest uppercase text-white/40">
          Campos estructurados · déjalos vacíos si aún no tienes el contenido
        </p>
        {(schema || []).map(({ key, label }) => (
          <div key={key} className="space-y-1" data-testid={`bible-field-wrapper-${key}`}>
            <div className="flex items-center justify-between">
              <label className="font-jetbrains text-[10px] tracking-widest uppercase text-white/50">{label}</label>
              {supportsMeta && (
                <button
                  type="button"
                  onClick={() => setExpandedMeta(m => ({ ...m, [key]: !m[key] }))}
                  data-testid={`bible-meta-toggle-${key}`}
                  className="font-jetbrains text-[9px] tracking-widest uppercase text-white/40 hover:text-neon-red"
                >
                  {expandedMeta[key] ? '− Ocultar procedencia' : '+ Procedencia'}
                </button>
              )}
            </div>
            <textarea
              value={fields[key] || ''}
              onChange={e => setFields(prev => ({ ...prev, [key]: e.target.value }))}
              data-testid={`bible-field-${key}`}
              rows={2}
              className="w-full bg-black/60 border border-white/10 px-3 py-2 text-white font-jetbrains text-xs focus:border-neon-red outline-none resize-y"
            />
            {supportsMeta && expandedMeta[key] && (
              <div className="grid grid-cols-2 gap-2 p-2 border border-white/10 bg-black/30" data-testid={`bible-field-meta-${key}`}>
                <div>
                  <label className="font-jetbrains text-[9px] uppercase text-white/40 tracking-widest">Tipo de fuente</label>
                  <select
                    value={fieldMeta[key]?.source_type || ''}
                    onChange={e => setFieldMeta(m => ({ ...m, [key]: { ...m[key], source_type: e.target.value } }))}
                    data-testid={`bible-meta-source_type-${key}`}
                    className="mt-0.5 w-full bg-black/60 border border-white/10 px-2 py-1 text-white font-jetbrains text-[11px] focus:border-neon-red outline-none"
                  >
                    <option value="">—</option>
                    {(sourceTypes || []).map(t => <option key={t} value={t}>{t}</option>)}
                  </select>
                </div>
                <div>
                  <label className="font-jetbrains text-[9px] uppercase text-white/40 tracking-widest">Confianza</label>
                  <select
                    value={fieldMeta[key]?.confidence || ''}
                    onChange={e => setFieldMeta(m => ({ ...m, [key]: { ...m[key], confidence: e.target.value } }))}
                    data-testid={`bible-meta-confidence-${key}`}
                    className="mt-0.5 w-full bg-black/60 border border-white/10 px-2 py-1 text-white font-jetbrains text-[11px] focus:border-neon-red outline-none"
                  >
                    <option value="">—</option>
                    {(confidenceLevels || []).map(c => <option key={c} value={c}>{c}</option>)}
                  </select>
                </div>
                <div className="col-span-2">
                  <label className="font-jetbrains text-[9px] uppercase text-white/40 tracking-widest">Referencia (fuente)</label>
                  <input
                    type="text"
                    value={fieldMeta[key]?.source_reference || ''}
                    onChange={e => setFieldMeta(m => ({ ...m, [key]: { ...m[key], source_reference: e.target.value } }))}
                    data-testid={`bible-meta-source_reference-${key}`}
                    className="mt-0.5 w-full bg-black/60 border border-white/10 px-2 py-1 text-white font-jetbrains text-[11px] focus:border-neon-red outline-none"
                  />
                </div>
                <div className="col-span-2">
                  <label className="font-jetbrains text-[9px] uppercase text-white/40 tracking-widest">Notas</label>
                  <textarea
                    value={fieldMeta[key]?.notes || ''}
                    onChange={e => setFieldMeta(m => ({ ...m, [key]: { ...m[key], notes: e.target.value } }))}
                    data-testid={`bible-meta-notes-${key}`}
                    rows={1}
                    className="mt-0.5 w-full bg-black/60 border border-white/10 px-2 py-1 text-white font-jetbrains text-[11px] focus:border-neon-red outline-none resize-y"
                  />
                </div>
              </div>
            )}
          </div>
        ))}
      </div>

      {!isEdit && (
        <label className="flex items-center gap-2 pt-2 cursor-pointer">
          <input
            type="checkbox"
            checked={activate}
            onChange={e => setActivate(e.target.checked)}
            data-testid="bible-activate-on-create"
          />
          <span className="font-jetbrains text-[11px] text-white/70">
            Marcar esta versión como <strong>activa</strong> al guardar
          </span>
        </label>
      )}

      {isEdit && initialData && (
        <div className="pt-2 border-t border-white/10">
          <p className="font-jetbrains text-[10px] tracking-widest uppercase text-white/40 mb-1">Texto libre</p>
          <textarea
            value={rawText}
            onChange={e => setRawText(e.target.value)}
            data-testid="bible-raw-text-edit"
            rows={6}
            className="w-full bg-black/60 border border-white/10 px-3 py-2 text-white font-jetbrains text-xs focus:border-neon-red outline-none resize-y"
          />
        </div>
      )}
    </div>
  );
};

// ==================== import (file upload) ====================
const BibleImport = ({ projectSlug, onBack, onSaved }) => {
  const [file, setFile] = useState(null);
  const [name, setName] = useState('');
  const [activate, setActivate] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState(null);
  const inputRef = useRef(null);

  const submit = async () => {
    if (!file) { setError('Elige un archivo'); return; }
    setUploading(true);
    setError(null);
    try {
      const form = new FormData();
      form.append('file', file);
      if (name) form.append('name', name);
      if (activate) form.append('activate', 'true');
      const res = await fetch(`${API}/projects/${projectSlug}/bibles/import`, {
        method: 'POST',
        credentials: 'include',
        body: form,
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const data = await res.json();
      onSaved(data.bible);
    } catch (e) {
      setError('No se pudo importar: ' + e.message);
    } finally {
      setUploading(false);
    }
  };

  return (
    <div className="space-y-4" data-testid="bible-import">
      <button
        onClick={onBack}
        data-testid="bible-import-back"
        className="flex items-center gap-1 font-jetbrains text-[10px] tracking-widest uppercase text-white/60 hover:text-white"
      >
        <ArrowLeft className="w-3 h-3" /> Volver
      </button>

      <div className="p-3 border border-white/10 bg-black/40">
        <p className="font-jetbrains text-[11px] text-white/70 leading-relaxed">
          Formatos aceptados: <strong>.txt</strong>, <strong>.md</strong>, <strong>.docx</strong>, <strong>.pdf</strong>.
          El texto se guarda como versión sin sobrescribir la anterior; los campos estructurados quedan vacíos hasta que los rellenes.
        </p>
      </div>

      <div>
        <label className="font-jetbrains text-[10px] tracking-widest uppercase text-white/50">Archivo</label>
        <div className="mt-1 flex items-center gap-2">
          <button
            onClick={() => inputRef.current?.click()}
            data-testid="bible-import-choose"
            className="px-3 py-2 bg-black/60 border border-white/10 hover:border-neon-red text-white font-jetbrains text-xs"
          >
            {file ? 'Cambiar archivo' : 'Elegir archivo'}
          </button>
          <input
            ref={inputRef}
            type="file"
            className="hidden"
            accept=".txt,.md,.docx,.pdf"
            onChange={e => setFile(e.target.files?.[0] || null)}
            data-testid="bible-import-file-input"
          />
          {file && (
            <span className="font-jetbrains text-xs text-white/70 truncate" data-testid="bible-import-filename">
              {file.name} ({Math.round(file.size / 1024)} KB)
            </span>
          )}
        </div>
      </div>

      <div>
        <label className="font-jetbrains text-[10px] tracking-widest uppercase text-white/50">Nombre (opcional)</label>
        <input
          type="text"
          value={name}
          onChange={e => setName(e.target.value)}
          data-testid="bible-import-name"
          placeholder={file?.name || 'Nombre de la versión'}
          className="mt-1 w-full bg-black/60 border border-white/10 px-3 py-2 text-white font-jetbrains text-sm focus:border-neon-red outline-none"
        />
      </div>

      <label className="flex items-center gap-2 cursor-pointer">
        <input
          type="checkbox"
          checked={activate}
          onChange={e => setActivate(e.target.checked)}
          data-testid="bible-import-activate"
        />
        <span className="font-jetbrains text-[11px] text-white/70">
          Marcar esta versión como <strong>activa</strong> al importar
        </span>
      </label>

      {error && (
        <div className="p-2 border border-neon-red/50 bg-neon-red/10 font-jetbrains text-xs text-neon-red" data-testid="bible-import-error">
          <AlertTriangle className="inline w-3 h-3 mr-1" /> {error}
        </div>
      )}

      <button
        onClick={submit}
        disabled={uploading || !file}
        data-testid="bible-import-submit"
        className="w-full flex items-center justify-center gap-1.5 px-3 py-2 bg-neon-red/20 border border-neon-red hover:bg-neon-red/40 text-white font-jetbrains text-xs tracking-widest uppercase disabled:opacity-50"
      >
        <Upload className="w-3 h-3" /> {uploading ? 'Importando...' : 'Importar'}
      </button>
    </div>
  );
};

// ==================== wizard root ====================
const BibleWizard = ({ open, onClose, projectSlug, projectName }) => {
  const [view, setView] = useState('list');   // list | create | paste | import | edit
  const [bibles, setBibles] = useState([]);
  const [schema, setSchema] = useState([]);
  const [supportsMeta, setSupportsMeta] = useState(false);
  const [sourceTypes, setSourceTypes] = useState([]);
  const [confidenceLevels, setConfidenceLevels] = useState([]);
  const [current, setCurrent] = useState(null);   // for edit
  const [loading, setLoading] = useState(false);
  const [notice, setNotice] = useState(null);

  const load = useCallback(async () => {
    if (!projectSlug) return;
    setLoading(true);
    try {
      const [schemaRes, listRes] = await Promise.all([
        fetch(`${API}/projects/${projectSlug}/bibles/schema`, { credentials: 'include' }),
        fetch(`${API}/projects/${projectSlug}/bibles`, { credentials: 'include' }),
      ]);
      if (schemaRes.ok) {
        const sd = await schemaRes.json();
        setSchema(sd.fields || []);
        setSupportsMeta(!!sd.supports_field_meta);
        setSourceTypes(sd.valid_source_types || []);
        setConfidenceLevels(sd.valid_confidence || []);
      }
      if (listRes.ok) setBibles((await listRes.json()).bibles || []);
    } catch (e) {
      console.error('Bible wizard load:', e);
    } finally {
      setLoading(false);
    }
  }, [projectSlug]);

  useEffect(() => {
    if (open) { setView('list'); setCurrent(null); setNotice(null); load(); }
  }, [open, load]);

  const openEdit = async (bible_id) => {
    try {
      const res = await fetch(`${API}/bibles/${bible_id}`, { credentials: 'include' });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const data = await res.json();
      setCurrent(data.bible);
      setView('edit');
    } catch (e) {
      setNotice({ kind: 'error', text: 'No se pudo abrir la Bible.' });
    }
  };

  const activate = async (bible_id) => {
    const res = await fetch(`${API}/bibles/${bible_id}/activate`, {
      method: 'POST',
      credentials: 'include',
    });
    if (res.ok) {
      setNotice({ kind: 'ok', text: 'Bible activa actualizada.' });
      load();
    } else {
      setNotice({ kind: 'error', text: 'No se pudo activar.' });
    }
  };

  const del = async (bible_id) => {
    if (!confirm('¿Borrar esta versión? Esta acción no se puede deshacer.')) return;
    const res = await fetch(`${API}/bibles/${bible_id}`, {
      method: 'DELETE', credentials: 'include',
    });
    if (res.ok) {
      setNotice({ kind: 'ok', text: 'Versión borrada.' });
      load();
    } else {
      const body = await res.json().catch(() => ({}));
      setNotice({ kind: 'error', text: body?.detail || 'No se pudo borrar.' });
    }
  };

  const onSaved = () => {
    setNotice({ kind: 'ok', text: 'Guardado correctamente.' });
    setCurrent(null);
    setView('list');
    load();
  };

  return (
    <AnimatePresence>
      {open && (
        <>
          <motion.div
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            transition={{ duration: 0.2 }}
            className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50"
            onClick={onClose}
            data-testid="bible-wizard-backdrop"
          />
          <motion.div
            initial={{ y: '100%' }} animate={{ y: 0 }} exit={{ y: '100%' }}
            transition={{ duration: 0.3, ease: 'easeOut' }}
            className="fixed inset-x-4 top-8 bottom-8 sm:inset-x-auto sm:right-8 sm:w-[560px] sm:top-12 sm:bottom-12 bg-[#0A0A0A] border border-neon-red/40 z-50 flex flex-col shadow-[0_0_60px_rgba(255,0,51,0.3)]"
            data-testid="bible-wizard"
          >
            <div className="flex items-center justify-between px-5 py-3 border-b border-neon-red/20 bg-black/40 backdrop-blur-xl">
              <div className="flex items-center gap-3 min-w-0">
                <BookOpen className="w-5 h-5 text-neon-red flex-shrink-0" />
                <div className="min-w-0">
                  <h2 className="font-outfit text-lg font-bold text-white truncate">Bible · {projectName}</h2>
                  <p className="font-jetbrains text-[10px] text-white/50 tracking-widest uppercase">
                    Fuente persistente de verdad del agente
                  </p>
                </div>
              </div>
              <div className="flex items-center gap-1">
                <button
                  onClick={load}
                  data-testid="bible-wizard-refresh"
                  className="p-1.5 text-white/60 hover:text-white"
                  title="Actualizar"
                >
                  <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
                </button>
                <button
                  onClick={onClose}
                  data-testid="bible-wizard-close"
                  className="p-1.5 text-white/60 hover:text-white"
                  title="Cerrar"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>
            </div>

            {notice && (
              <div
                data-testid="bible-wizard-notice"
                className={`px-5 py-2 font-jetbrains text-[11px] border-b ${
                  notice.kind === 'ok'
                    ? 'text-emerald-300 border-emerald-400/30 bg-emerald-950/20'
                    : 'text-neon-red border-neon-red/30 bg-neon-red/10'
                }`}
              >
                {notice.text}
              </div>
            )}

            <div className="flex-1 overflow-y-auto p-5">
              {view === 'list' && (
                <VersionList
                  bibles={bibles}
                  loading={loading}
                  onOpen={openEdit}
                  onActivate={activate}
                  onDelete={del}
                  onCreate={() => { setCurrent(null); setView('create'); }}
                  onPaste={() => { setCurrent(null); setView('paste'); }}
                  onImport={() => setView('import')}
                />
              )}
              {(view === 'create' || view === 'paste') && (
                <BibleEditor
                  projectSlug={projectSlug}
                  bibleId={null}
                  initialData={null}
                  schema={schema}
                  supportsMeta={supportsMeta}
                  sourceTypes={sourceTypes}
                  confidenceLevels={confidenceLevels}
                  mode={view}
                  onBack={() => setView('list')}
                  onSaved={onSaved}
                />
              )}
              {view === 'import' && (
                <BibleImport
                  projectSlug={projectSlug}
                  onBack={() => setView('list')}
                  onSaved={onSaved}
                />
              )}
              {view === 'edit' && current && (
                <BibleEditor
                  projectSlug={projectSlug}
                  bibleId={current.bible_id}
                  initialData={current}
                  schema={schema}
                  supportsMeta={supportsMeta}
                  sourceTypes={sourceTypes}
                  confidenceLevels={confidenceLevels}
                  mode="edit"
                  onBack={() => { setCurrent(null); setView('list'); }}
                  onSaved={onSaved}
                />
              )}
            </div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
};

export default BibleWizard;
